import { Box, Button, Typography, Divider } from '@mui/material';
import { BorderBox } from '../components/layout/BorderBox';
import { LoginButton } from '../components/Buttons/LoginButton';
import { SmartLoginButton } from '../components/Buttons/SmartLoginButton';
import { useThemeContext } from '../context/theme/context';
import { useAuth0 } from '@auth0/auth0-react';
import { useState, useEffect } from 'react';
import type { LastUserLogin } from '../types/auth';
import { getLastUserLogin } from '../types/auth';
import HipaaLogo from '@assets/hipaa-logo.svg?react';
import { saveReturnToUrl } from '@utils/LocalStorageUtils';

interface LoginViewProps {
  /** Custom title. If undefined, uses dynamic "Welcome back!" / "Welcome to..." */
  title?: string;
  /** Custom subtitle. If undefined, uses dynamic messaging based on user data */
  subtitle?: string | React.ReactNode;
  /** Fallback title when no lastUserLogin and no custom title */
  fallbackTitle?: string;
  /** Fallback subtitle when no lastUserLogin and no custom subtitle */
  fallbackSubtitle?: string | React.ReactNode;
}

const LoginView = ({ title, subtitle, fallbackTitle, fallbackSubtitle }: LoginViewProps = {}) => {
  const { brandLanguage } = useThemeContext();
  const { loginWithRedirect } = useAuth0();
  const [lastUserLogin, setLastUserLogin] = useState<LastUserLogin>({});

  useEffect(() => {
    // Load last user login data on component mount with validation
    const validatedLogin = getLastUserLogin();
    setLastUserLogin(validatedLogin);
  }, []);

  const handleSignInDifferent = () => {
    // Clear the stored user info
    localStorage.removeItem('lastUserLogin');

    // Save the current URL so we can redirect back after login
    saveReturnToUrl();

    loginWithRedirect({
      authorizationParams: {
        prompt: 'login', // Forces login screen even if session exists
      },
    });
  };

  const handleNotYou = () => {
    // Clear stored user info for privacy on shared devices
    localStorage.removeItem('lastUserLogin');
    setLastUserLogin({}); // Update state to re-render
  };

  // Compute display title and subtitle based on props and user data
  const displayTitle =
    title !== undefined
      ? title
      : lastUserLogin.email
        ? 'Welcome back!'
        : fallbackTitle || `Welcome to ${brandLanguage.brandName}!`;

  const displaySubtitle =
    subtitle !== undefined
      ? subtitle
      : lastUserLogin.email
        ? 'Continue where you left off'
        : fallbackSubtitle || (
            <>
              To begin experiencing the future of automated charting, please login or create an
              account by clicking the button below.
            </>
          );

  return (
    <Box
        sx={{
          minHeight: '100%',
          bgcolor: '#F4F1F8',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          py: { xs: 4, sm: 8 },
          px: { xs: 2, sm: 3 },
        }}
      >
        <Box sx={{ width: '100%', maxWidth: '600px' }}>
          <BorderBox>
            <Box sx={{ textAlign: 'center' }}>
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 700,
                  mb: 2,
                  fontSize: { xs: '1.75rem', sm: '2.5rem' },
                }}
              >
                {displayTitle}
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: 'text.secondary',
                  mb: 4,
                  mx: 'auto',
                  maxWidth: '450px',
                  fontSize: { xs: '0.9375rem', sm: '1rem' },
                  lineHeight: 1.6,
                }}
              >
                {displaySubtitle}
              </Typography>

              {lastUserLogin.email ? (
                <>
                  <SmartLoginButton lastUserLogin={lastUserLogin} />
                  <Box sx={{ mt: 3, mb: 2 }}>
                    <Divider sx={{ width: '200px', mx: 'auto' }}>
                      <Typography variant="caption" color="text.secondary">
                        OR
                      </Typography>
                    </Divider>
                  </Box>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleSignInDifferent}
                    sx={{ textTransform: 'none', mb: 1 }}
                  >
                    Sign in differently
                  </Button>
                  <Box>
                    <Button
                      variant="text"
                      size="small"
                      onClick={handleNotYou}
                      sx={{ textTransform: 'none', fontSize: '0.75rem', color: 'text.secondary' }}
                    >
                      Not you?
                    </Button>
                  </Box>
                </>
              ) : (
                <LoginButton />
              )}
            </Box>
          </BorderBox>
          <Box sx={{ mt: 6, display: 'flex', justifyContent: 'center' }}>
            <HipaaLogo height={43} fill="rgb(200, 173, 225)" aria-label="HIPAA Compliant" />
          </Box>
        </Box>
      </Box>
  );
};

export default LoginView;
